import { IS_DEV } from "@/constants/Dev.constant";

/**
 * Override to force enable/disable debug logging.
 * - `undefined`: Use IS_DEV (default behavior)
 * - `true`: Force enable debug logs
 * - `false`: Force disable debug logs (most efficient - no-op functions)
 */
const DEBUG_OVERRIDE: boolean | undefined = undefined;
// const DEBUG_OVERRIDE: boolean | undefined = false;

/**
 * Whether debug logging is enabled.
 * Determined once at module load for efficiency.
 */
const DEBUG_ENABLED = DEBUG_OVERRIDE ?? IS_DEV;

// No-op function - does nothing, used when debug is disabled
const noop = (..._args: unknown[]): void => {};

/**
 * Debug method type with chainable .off property for selective disabling
 */
type DebugMethod = {
  (...args: unknown[]): void;
  /** Call .off to skip this specific log (no-op) */
  off: (...args: unknown[]) => void;
};

/**
 * Creates a debug method with an .off chain for selective disabling
 */
const createDebugMethod = (
  consoleMethod: (...args: unknown[]) => void
): DebugMethod => Object.assign(consoleMethod.bind(console), { off: noop });

/** No-op debug method (used when debugging is disabled) */
const noopMethod: DebugMethod = Object.assign(noop, { off: noop });

/**
 * Debug logging utility that only logs in development environment.
 * Use this instead of console.log to avoid polluting production logs.
 *
 * When disabled (production or DEBUG_OVERRIDE = false), all methods are no-ops
 * with zero runtime overhead - no condition checks on each call.
 *
 * Each method has a chainable `.off` property to selectively disable that call:
 *
 * @example
 * debug.log("User clicked:", data);
 * debug.log.off("Temporarily disabled log"); // no-op, won't log
 * debug.warn("Deprecated API:", endpoint);
 * debug.warn.off("Skipped warning"); // no-op
 * debug.error("Failed to fetch:", error);
 * debug.info("Loading state:", isLoading);
 * debug.group("Component render");
 * debug.groupEnd();
 * debug.table(data);
 */
export const debug = DEBUG_ENABLED
  ? {
      /** Log general messages - equivalent to console.log */
      // eslint-disable-next-line no-console
      log: createDebugMethod(console.log),

      /** Log warning messages - equivalent to console.warn */
      // eslint-disable-next-line no-console
      warn: createDebugMethod(console.warn),

      /** Log error messages - equivalent to console.error */
      // eslint-disable-next-line no-console
      error: createDebugMethod(console.error),

      /** Log info messages - equivalent to console.info */
      // eslint-disable-next-line no-console
      info: createDebugMethod(console.info),

      /** Start a console group - equivalent to console.group */
      // eslint-disable-next-line no-console
      group: createDebugMethod(console.group),

      /** Start a collapsed console group - equivalent to console.groupCollapsed */
      // eslint-disable-next-line no-console
      groupCollapsed: createDebugMethod(console.groupCollapsed),

      /** End a console group - equivalent to console.groupEnd */
      // eslint-disable-next-line no-console
      groupEnd: createDebugMethod(console.groupEnd),

      /** Display tabular data - equivalent to console.table */
      // eslint-disable-next-line no-console
      table: createDebugMethod(console.table),

      /** Log with trace - equivalent to console.trace */
      // eslint-disable-next-line no-console
      trace: createDebugMethod(console.trace),

      /** Log debug messages - equivalent to console.debug */
      // eslint-disable-next-line no-console
      debug: createDebugMethod(console.debug),

      /** Start a timer - equivalent to console.time */
      // eslint-disable-next-line no-console
      time: createDebugMethod(console.time),

      /** End a timer and log the duration - equivalent to console.timeEnd */
      // eslint-disable-next-line no-console
      timeEnd: createDebugMethod(console.timeEnd),

      /** Log a timer without ending it - equivalent to console.timeLog */
      // eslint-disable-next-line no-console
      timeLog: createDebugMethod(console.timeLog),

      ENABLED: DEBUG_ENABLED
    }
  : {
      // When disabled, all methods are no-ops - no runtime overhead
      log: noopMethod,
      warn: noopMethod,
      error: noopMethod,
      info: noopMethod,
      group: noopMethod,
      groupCollapsed: noopMethod,
      groupEnd: noopMethod,
      table: noopMethod,
      trace: noopMethod,
      debug: noopMethod,
      time: noopMethod,
      timeEnd: noopMethod,
      timeLog: noopMethod,
      ENABLED: DEBUG_ENABLED
    };
